import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import JsonView from "@uiw/react-json-view";
import { lightTheme } from "@uiw/react-json-view/light";
import DocumentPreview from "./DocumentPreview";
import {
  confColor,
  ocrPalette,
  statusColor,
  statusText,
  toneFor,
} from "./ocrPalette";
import OCRLanguageChip from "./ocr-language-chip";
import PDFViewer from "../../Common/PDFViewer";

// JSON viewer theme aligned with the OCR palette.
const jsonViewerTheme = {
  ...lightTheme,
  "--w-rjv-font-family": "monospace",
  "--w-rjv-background-color": "transparent",
  "--w-rjv-color": ocrPalette.text,
  "--w-rjv-key-string": ocrPalette.accent,
  "--w-rjv-line-color": ocrPalette.border,
  "--w-rjv-arrow-color": ocrPalette.muted,
  "--w-rjv-info-color": ocrPalette.muted,
  "--w-rjv-colon-color": ocrPalette.muted,
  "--w-rjv-brackets-color": ocrPalette.muted,
  "--w-rjv-curlybraces-color": ocrPalette.muted,
  "--w-rjv-quotes-color": ocrPalette.muted,
  "--w-rjv-type-string-color": ocrPalette.ok,
  "--w-rjv-type-int-color": ocrPalette.accent,
  "--w-rjv-type-float-color": ocrPalette.accent,
  "--w-rjv-type-bigint-color": ocrPalette.accent,
  "--w-rjv-type-boolean-color": ocrPalette.purple,
  "--w-rjv-type-null-color": ocrPalette.fail,
  "--w-rjv-type-undefined-color": ocrPalette.muted,
};

/**
 * OcrResultView — generic, template-driven OCR result screen (MUI 5).
 *
 * Props:
 *  result: {
 *    documentType: { label, confidence },          // confidence 0–100
 *    languages: string[],
 *    textModes: string[],                          // e.g. ["printed","handwritten"]
 *    processingSecondsPerPage: number,
 *    quality: { score, dpi, blur, notes? },        // score 0–100
 *    fraudChecks: [{ id, label, status, message? }] // status: "pass"|"warn"|"fail"
 *    fields: [{
 *      id, label, value, confidence,               // confidence 0–100
 *      status,                                     // "verified"|"review"|"failed"|"none"
 *      page,                                       // 1-based page number
 *      bbox                                        // [x, y, w, h] normalized 0–1, optional
 *    }],
 *    pages: [{ src, alt? }],                       // image URLs per page
 *  }
 *  reviewThreshold?: number  (default 95)
 *  collapsedFieldCount?: number (default 8)
 *  onExport?: (result) => void
 *  onSend?: (result) => void   // webhook / downstream system push
 */
export default function OcrResultView({
  result,
  reviewThreshold = 95,
  collapsedFieldCount = 8,
  onExport = null,
  onSend = null,
  previewImage,
  onReset,
  isPdf = false
}) {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("md"));

  const [previewOpen, setPreviewOpen] = useState(true);
  const [activeFieldId, setActiveFieldId] = useState(null);
  const [activePage, setActivePage] = useState(1);
  const [showAllFields, setShowAllFields] = useState(false);
  const [dataTab, setDataTab] = useState("fields"); // fields | json
  const [expandedValueFieldId, setExpandedValueFieldId] = useState(null);
  const [showWarningList, setToggleWarningList] = useState(false);

  const {
    documentType,
    languages = [],
    textModes = [],
    processingSecondsPerPage,
    quality,
    fraudChecks = [],
    fields = [],
    pages = [],
  } = result;

  const warnings = useMemo(
    () => fraudChecks.filter((c) => c.status !== "pass"),
    [fraudChecks]
  );
  const passCount = fraudChecks.length - warnings.length;
  const reviewFields = useMemo(
    () => fields.filter((f) => f.status === "review" || f.confidence < reviewThreshold),
    [fields, reviewThreshold]
  );

  const visibleFields = showAllFields ? fields : fields.slice(0, collapsedFieldCount);
  const hiddenCount = fields.length - visibleFields.length;

  const activeField = fields.find((f) => f.id === activeFieldId) || null;

  function locateField(field) {
    setActiveFieldId(field.id);
    if (field.page && field.page !== activePage) setActivePage(field.page);
    if (!previewOpen) setPreviewOpen(true);
  }

  const showPreview = previewOpen && previewImage !== null;

  const tableHeadName = (cell) => {
    return cell.replace(/_/g, " ").replace(/\b\w/g, char => char.toUpperCase());
  }

  return (
    <Box
      sx={{
        bgcolor: ocrPalette.bg,
        borderRadius: "12px",
        p: 2,
        color: ocrPalette.text,
        fontSize: 13,
      }}
    >
      {/* ---- Header strip ---- */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1.5}
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: ocrPalette.text }}>
            {documentType?.label || "Unknown document"}
          </Typography>
          {documentType?.confidence != null && (
            <OcrChip label={`${documentType.confidence.toFixed(1)}%`} tone="green" />
          )}
          {languages.length > 0 && (
            <OCRLanguageChip
              label={
                languages.length === 1 ? languages[0] : `${languages.length} languages`
              }
              languages={languages}
              tone="blue"
            />
          )}
          {/* {textModes.length > 0 && (
            <OcrChip sx={{ textTransform: 'capitalize' }} label={textModes.join(" + ")} tone="purple" />
          )}
          {processingSecondsPerPage != null && (
            <OcrChip label={`${processingSecondsPerPage}s/page`} tone="gray" />
          )} */}
        </Stack>
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
          <OcrButton onClick={() => setPreviewOpen((v) => !v)}>
            {previewOpen ? "Hide preview" : "Show preview"}
          </OcrButton>
          {
            onReset ? <OcrButton onClick={onReset}>Clear</OcrButton> : null
          }
        </Stack>
      </Stack>

      {/* ---- Split layout: fixed preview strip + fluid data panel ---- */}
      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          gridTemplateColumns:
            showPreview && !isNarrow ? "280px minmax(0, 1fr)" : "minmax(0, 1fr)",
        }}
      >
        {showPreview && (
          <Box sx={{ order: isNarrow ? -1 : 0 }}>
            {
              isPdf ?
                <PDFViewer src={previewImage}/>
                :
                <DocumentPreview
                  pages={[{ src: previewImage, alt: "Preview" }]}
                  activePage={activePage}
                  onPageChange={setActivePage}
                  highlight={
                    activeField && activeField.bbox && activeField.page === activePage
                      ? { bbox: activeField.bbox, tone: toneFor(activeField, reviewThreshold) }
                      : null
                  }
                />
            }
          </Box>
        )}

        <Stack spacing={1.25} sx={{ minWidth: 0 }}>
          {/* Summary cards */}
          <Box
            sx={{
              display: "grid",
              gap: 1.25,
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            }}
          >
            <SummaryCard label="Fraud & tamper">
              {warnings.length > 0 ? (
                <Box component="button" onClick={() => setToggleWarningList((prev) => !prev)} sx={{ color: ocrPalette.warn, backgroundColor: 'transparent', p: 0, border: 'none', cursor: 'pointer' }}>
                  {warnings.length} warning{warnings.length > 1 ? "s" : ""}
                </Box>
              ) : (
                <Box component="span" sx={{ color: ocrPalette.ok }}>
                  All pass
                </Box>
              )}
              <CardSub> · {passCount} pass</CardSub>
            </SummaryCard>

            <SummaryCard label="Quality">
              {quality?.confidence ?? "\u2014"}/100
              {quality?.dpi && <CardSub> · {quality.dpi} dpi</CardSub>}
            </SummaryCard>

            <SummaryCard label="Fields">
              {fields.length} extracted
              {/* {reviewFields.length > 0 && (
                <CardSub sx={{ color: ocrPalette.warn }}>
                  {" "}· {reviewFields.length} review
                </CardSub>
              )} */}
            </SummaryCard>
          </Box>

          {/* Warning bars */}

          <Stack
            direction='column'
            spacing={1}
            sx={{ transition: 'all 0.5s linear', opacity: showWarningList ? 1 : 0, height: showWarningList ? 'auto' : 0, overflow: 'hidden' }}
          >
            {
              showWarningList ?
                warnings.map((w) => {
                  const linked = fields.find((f) => f.id === w.fieldId);
                  return (
                    <Stack
                      key={w.id}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={1}
                      sx={{
                        bgcolor: ocrPalette.warnBg,
                        color: ocrPalette.warn,
                        borderRadius: "8px",
                        px: 1.5,
                        py: 1,
                        fontSize: 12,
                      }}
                    >
                      <Box component="span">{w.label}</Box>
                      {linked && linked.bbox && (
                        <Button
                          onClick={() => locateField(linked)}
                          sx={{
                            flexShrink: 0,
                            textTransform: "none",
                            fontSize: 11,
                            px: 1,
                            py: 0.25,
                            minWidth: 0,
                            borderRadius: "7px",
                            border: "1px solid #ef9f27",
                            color: ocrPalette.warn,
                            bgcolor: "transparent",
                            "&:hover": { bgcolor: "rgba(239,159,39,0.12)" },
                          }}
                        >
                          Locate
                        </Button>
                      )}
                    </Stack>
                  );
                })
                : <></>
            }
          </Stack>

          {/* Field table */}
          <Paper
            elevation={0}
            sx={{
              bgcolor: ocrPalette.surface,
              border: `1px solid ${ocrPalette.border}`,
              borderRadius: "10px",
              px: 1.75,
              py: 1.25,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1}
              sx={{ mb: 0.75 }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: ocrPalette.text }}>
                Extracted fields
                {/* <Box
                  component="span"
                  sx={{ fontSize: 11, fontWeight: 400, color: ocrPalette.muted }}
                >
                  {" "}— click a row to locate on document
                </Box> */}
              </Typography>
              <Stack direction="row" spacing={0.75}>
                <TabButton active={dataTab === "fields"} onClick={() => setDataTab("fields")}>
                  Fields
                </TabButton>
                <TabButton active={dataTab === "json"} onClick={() => setDataTab("json")}>
                  JSON
                </TabButton>
              </Stack>
            </Stack>

            {dataTab === "fields" ? (
              <Box sx={{ overflowX: "auto" }}>
                <Table
                  size="small"
                  sx={{
                    tableLayout: "fixed",
                    "& td, & th": { borderBottom: "none", px: 0, py: 0.75 },
                  }}
                >
                  <TableHead>
                    <TableRow sx={{ "& th": { borderBottom: `1px solid ${ocrPalette.border}` } }}>
                      <HeadCell sx={{ width: "30%" }}>Field</HeadCell>
                      <HeadCell sx={{ width: "54%" }}>Value</HeadCell>
                      <HeadCell align="right" sx={{ width: "16%" }}>
                        Conf.
                      </HeadCell>
                      {/* <HeadCell align="right" sx={{ width: "20%" }}>
                        Status
                      </HeadCell> */}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleFields.map((f) => {
                      const isActive = f.id === activeFieldId;
                      return (
                        <TableRow
                          key={f.id}
                          hover
                          onClick={() => locateField(f)}
                          sx={{
                            cursor: "pointer",
                            borderTop: `1px solid ${ocrPalette.border}`,
                            bgcolor: isActive ? ocrPalette.rowActive : "transparent",
                            "&:hover": {
                              bgcolor: isActive ? ocrPalette.rowActive : ocrPalette.hover,
                            },
                          }}
                        >
                          <TableCell sx={{ fontSize: 12, color: ocrPalette.muted }}>
                            {f.label}
                          </TableCell>
                          <FieldValueCell
                            fieldId={f.id}
                            value={f.value}
                            expanded={expandedValueFieldId === f.id}
                            onToggle={(id) =>
                              setExpandedValueFieldId((prev) => (prev === id ? null : id))
                            }
                          />
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: 12,
                              color: confColor(f.confidence, reviewThreshold),
                            }}
                          >
                            {f.confidence?.toFixed(1)}
                          </TableCell>
                          {/* <TableCell
                            align="right"
                            sx={{ fontSize: 12, color: statusColor(f.status) }}
                          >
                            {statusText(f.status)}
                          </TableCell> */}
                        </TableRow>
                      );
                    })}
                    {hiddenCount > 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          sx={{ fontSize: 11, color: ocrPalette.muted, borderTop: `1px solid ${ocrPalette.border}` }}
                        >
                          + {hiddenCount} more fields{" "}
                          <OcrLink onClick={() => setShowAllFields(true)}>show all</OcrLink>
                        </TableCell>
                      </TableRow>
                    )}
                    {showAllFields && fields.length > collapsedFieldCount && (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          sx={{ fontSize: 11, color: ocrPalette.muted, borderTop: `1px solid ${ocrPalette.border}` }}
                        >
                          <OcrLink onClick={() => setShowAllFields(false)}>collapse</OcrLink>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            ) : (
              <Box
                sx={{
                  maxHeight: 360,
                  overflow: "auto",
                  bgcolor: ocrPalette.hover,
                  borderRadius: "6px",
                  p: 1.25,
                  fontSize: 12,
                }}
              >
                <JsonView
                  value={result}
                  style={jsonViewerTheme}
                  collapsed={2}
                  displayDataTypes={false}
                  enableClipboard
                />
              </Box>
            )}
          </Paper>

          {/* Footer actions */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}
          >
            <Typography sx={{ fontSize: 11, color: ocrPalette.muted }}>
              {reviewFields.length} field{reviewFields.length !== 1 ? "s" : ""} below review
              threshold ({reviewThreshold}%)
            </Typography>
            <Stack direction="row" spacing={1} sx={{ ml: "auto" }}>
              {onExport && <OcrButton onClick={() => onExport(result)}>Export</OcrButton>}
              {onSend && (
                <OcrButton primary onClick={() => onSend(result)}>
                  Send to webhook
                </OcrButton>
              )}
            </Stack>
            {
              dataTab === "fields" && result.tables && result.tables.length > 0 ?
                result.tables.map((table, index) => (
                  <Paper key={index} elevation={0} sx={{ bgcolor: ocrPalette.surface, border: `1px solid ${ocrPalette.border}`, borderRadius: "8px", px: 1.5, py: 1.25 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: ocrPalette.text, mb: 1 }}>
                      {table.table_name}
                    </Typography>
                    <Table
                      key={index}
                      size="small"
                      sx={{
                        tableLayout: "fixed",
                        "& td, & th": { borderBottom: "none", px: 1, py: 0.75 },
                      }}
                    >
                      {
                        (table.rows && table.rows.length > 0) ? (
                          <TableHead>
                            <TableRow sx={{ "& th": { borderBottom: `1px solid ${ocrPalette.border}` } }}>
                              {
                                Object.keys(table.rows[0]).map((cell, index) => (
                                  <HeadCell key={index} sx={{ width: "30%", fontWeight: 500, fontSize: "13px" }}>{tableHeadName(cell)}</HeadCell>
                                ))
                              }
                            </TableRow>
                          </TableHead>
                        ) : <></>
                      }
                      <TableBody>
                        {
                          table.rows.map((row, index) => (
                            <TableRow key={index}
                              hover
                              // onClick={() => locateField(f)}
                              sx={{
                                cursor: "pointer",
                                borderTop: `1px solid ${ocrPalette.border}`,
                                bgcolor: "transparent",
                                "&:hover": {
                                  bgcolor: ocrPalette.hover,
                                },
                              }}
                            >
                              {
                                Object.keys(row).map((key) => {
                                  return <TableCell sx={{ fontSize: 12, color: ocrPalette.muted, overflowWrap: 'break-word' }} key={key}>{row[key]}</TableCell>
                                })
                              }
                            </TableRow>
                          ))
                        }
                      </TableBody>
                    </Table >
                  </Paper>
                ))
                : <></>
            }
          </Stack>
        </Stack>
      </Box>
    </Box >
  );
}

/* ---------------- Small presentational helpers ---------------- */

const chipTones = {
  green: { bg: ocrPalette.okBg, color: ocrPalette.ok },
  blue: { bg: ocrPalette.accentBg, color: ocrPalette.accent },
  purple: { bg: ocrPalette.purpleBg, color: ocrPalette.purple },
  gray: { bg: ocrPalette.grayBg, color: ocrPalette.grayText },
};

function OcrChip({ label, tone = "gray", sx = {} }) {
  const t = chipTones[tone] || chipTones.gray;
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        bgcolor: t.bg,
        color: t.color,
        fontSize: 12,
        height: 22,
        borderRadius: "10px",
        "& .MuiChip-label": { px: 1.25 },
        ...sx,
      }}
    />
  );
}

function OcrButton({ children, onClick, primary }) {
  return (
    <Button
      onClick={onClick}
      disableElevation
      sx={{
        textTransform: "none",
        fontSize: 12,
        px: 1.5,
        py: 0.625,
        minWidth: 0,
        borderRadius: "7px",
        border: primary
          ? `1px solid ${ocrPalette.accentBorder}`
          : `1px solid ${ocrPalette.border}`,
        bgcolor: primary ? ocrPalette.accentBg : ocrPalette.surface,
        color: primary ? ocrPalette.accent : ocrPalette.text,
        "&:hover": { bgcolor: primary ? ocrPalette.accentHover : ocrPalette.hover },
      }}
    >
      {children}
    </Button>
  );
}

function TabButton({ children, active, onClick }) {
  return (
    <Button
      onClick={onClick}
      disableElevation
      sx={{
        textTransform: "none",
        fontSize: 11,
        px: 1.25,
        py: 0.25,
        minWidth: 0,
        borderRadius: "8px",
        bgcolor: active ? ocrPalette.accentBg : "transparent",
        color: active ? ocrPalette.accent : ocrPalette.muted,
        "&:hover": { bgcolor: active ? ocrPalette.accentBg : "rgba(0,0,0,0.04)" },
      }}
    >
      {children}
    </Button>
  );
}

function SummaryCard({ label, children }) {
  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: ocrPalette.surface,
        border: `1px solid ${ocrPalette.border}`,
        borderRadius: "8px",
        px: 1.5,
        py: 1.25,
      }}
    >
      <Typography sx={{ m: 0, fontSize: 11, color: ocrPalette.muted }}>{label}</Typography>
      <Typography sx={{ mt: 0.5, fontSize: 14, fontWeight: 600, color: ocrPalette.text }}>
        {children}
      </Typography>
    </Paper>
  );
}

function CardSub({ children, sx }) {
  return (
    <Box
      component="span"
      sx={{ fontWeight: 400, fontSize: 11, color: ocrPalette.muted, ...sx }}
    >
      {children}
    </Box>
  );
}

function HeadCell({ children, align, sx }) {
  return (
    <TableCell
      align={align}
      sx={{ fontSize: 12, fontWeight: 500, color: ocrPalette.muted, ...sx }}
    >
      {children}
    </TableCell>
  );
}

function FieldValueCell({ fieldId, value, expanded, onToggle }) {
  const displayValue = value ?? "\u2014";

  return (
    <TableCell
      onClick={(e) => {
        e.stopPropagation();
        onToggle(fieldId);
      }}
      title={!expanded && displayValue !== "\u2014" ? displayValue : undefined}
      sx={{
        fontSize: 12,
        color: ocrPalette.text,
        maxWidth: 0,
        overflow: expanded ? "visible" : "hidden",
        textOverflow: expanded ? "clip" : "ellipsis",
        whiteSpace: expanded ? "normal" : "nowrap",
        wordBreak: expanded ? "break-word" : "normal",
        cursor: "pointer",
        verticalAlign: expanded ? "top" : "middle",
      }}
    >
      {displayValue}
    </TableCell>
  );
}

function OcrLink({ children, onClick }) {
  return (
    <Link
      component="button"
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      underline="hover"
      sx={{ fontSize: 11, color: ocrPalette.accent, verticalAlign: "baseline" }}
    >
      {children}
    </Link>
  );
}
